import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Chip, Grid, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, PassService } from '../../api';
import { getDateFormat } from '../../common/getDateAndTimeFormat';
import { EXPIRING_SOON_DAYS } from '../../passes/PassesFilter/PassesFilterContext';
import { getExpirationDate } from '../../passes/management/utils';
import { getPassOrderPage } from '../../utils/AccountUtils';

export const ExpiringPassesIndicator: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);

  const { data: expiringPasses, isLoading } = useSWR(
    'expiringPasses',
    () => PassService.getRenewalCandidatePasses(EXPIRING_SOON_DAYS),
    { refreshInterval: 300000 },
  );

  if (isLoading || !expiringPasses?.length) {
    return null;
  }

  const count = expiringPasses.length;
  const hasMoreThanOnePage = count >= 10;
  const displayCount = hasMoreThanOnePage ? '10+' : count.toString();

  const sortedByExpiration = expiringPasses
    .map((pass) => ({ pass, expirationDate: getExpirationDate(pass, getPassOrderPage(account, pass.order_page_id)) }))
    .filter((entry) => entry.expirationDate)
    .sort((a, b) => moment(a.expirationDate).valueOf() - moment(b.expirationDate).valueOf());

  const nextExpirationDate =
    sortedByExpiration.length > 0 ? moment(sortedByExpiration[0].expirationDate).format(shortDateFormat) : null;

  const handleClick = () => {
    navigate('/dashboard/passes?at_risk=1');
  };

  return (
    <Tooltip title={t('passes.expiring_tooltip')} arrow>
      <Grid onClick={handleClick} sx={{ cursor: 'pointer' }}>
        <Chip
          icon={<HourglassBottomIcon />}
          label={
            <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                {displayCount} {t('passes.expiring_label')}
              </Typography>
              {nextExpirationDate && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8, lineHeight: 1.1 }}>
                  {t('passes.expiring_next_label')}: {nextExpirationDate}
                </Typography>
              )}
            </Grid>
          }
          variant="outlined"
          size="small"
          sx={{
            height: 'auto',
            minHeight: nextExpirationDate ? 48 : 32,
            borderColor: 'primary.main',
            color: 'primary.main',
            '& .MuiChip-icon': {
              color: 'primary.main',
            },
            '& .MuiChip-label': {
              padding: '6px 8px',
            },
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'primary.main',
              '& .MuiChip-icon': {
                color: 'primary.main',
              },
            },
          }}
        />
      </Grid>
    </Tooltip>
  );
};
